// @ts-nocheck
import ChevronDown from "@store/Library/Components/Icons/ChevronDown";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Search from "@insite/mobius/Icons/Search";
import { AccordionSection, CheckboxGroup, GridContainer, TextField, Typography } from "@insite/mobius/index";
import useSearch from "@store/Framework/Common/Hooks/useSearch";
import { CompanyLocation, CustomerLocation } from "@store/Framework/Store/Bunzl/Pages/Reporting/LoadLocations";
import { locationsSelector } from "@store/Framework/Store/Bunzl/Pages/Reporting/ReportOrderFrequency/ReportOrderFrequencySelectors";
import { usePoError } from "@store/Framework/Store/Bunzl/Pages/Reporting/StoreHooks/usePoError";
import Styles from "@store/Library/Components/Reporting/LocationPicker/Styles";
import useLocation from "@store/Library/Components/Reporting/LocationPicker/useLocation";
import CheckboxModalPicker from "@store/Library/Components/Reporting/ModalPicker/CheckboxModalPicker";
import CheckboxModalPickerGroupTitle from "@store/Library/Components/Reporting/ModalPicker/CheckboxModalPickerGroupTitle";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { css } from "styled-components";

interface RenderCompanyProps {
    company: CompanyLocation;
    toggleAllCompanyCustomers: (customerLocations: CustomerLocation[]) => void;
    selectedItems: string[];
    toggleItem: (item: string) => void;
}

export const getCustomerLocationDisplayName = (customerLocation: CustomerLocation) => {
    return `${customerLocation.displayName} (${customerLocation.customerSequence})`;
};
const renderCompany: React.FC<RenderCompanyProps> = ({
    company,
    toggleAllCompanyCustomers,
    selectedItems,
    toggleItem,
}) => {
    return company.customers.length ? (
        <Styles.AccordionLocationPicker headingLevel={2} key={company.id}>
            <AccordionSection
                title={
                    <CheckboxModalPickerGroupTitle
                        company={company}
                        selectedItems={selectedItems}
                        toggleAllCompanyCustomers={toggleAllCompanyCustomers}
                    />
                }
            >
                <CheckboxGroup>
                    {company.customers.map((customerLocation: CustomerLocation) => {
                        return (
                            <CheckboxModalPicker
                                key={customerLocation.id}
                                identifier={customerLocation.id}
                                itemText={getCustomerLocationDisplayName(customerLocation)}
                                selectedItems={selectedItems}
                                toggleItem={toggleItem}
                            />
                        );
                    })}
                </CheckboxGroup>
            </AccordionSection>
        </Styles.AccordionLocationPicker>
    ) : null;
};

interface LocationPickerProps {
    isError: boolean;
}
const LocationPicker: React.FC<LocationPickerProps> = ({ isError = false }) => {
    const { selectedItems, toggleAllCompanyCustomers, toggleItem } = useLocation();
    const [poError] = usePoError();
    const locationData = useSelector(locationsSelector);
    const [filteredLocations, setFilteredLocations] = React.useState<CompanyLocation[] | undefined>();
    const searchCallback = (searchString: string) => {
        if (!searchString) {
            setFilteredLocations(locationData);
            return;
        }
        setFilteredLocations(
            locationData?.map(company => {
                return {
                    ...company,
                    customers: company.customers.filter(
                        customer =>
                            customer.customerSequence.toLowerCase().includes(searchString.toLowerCase()) ||
                            customer.name.toLowerCase().includes(searchString.toLowerCase()),
                    ),
                };
            }) || [],
        );
    };

    const [searchTerm, setSearchTerm] = useSearch(searchCallback);

    function getTotalCustomers() {
        return getAllCompanyCustomers().length;
    }
    function getAllCompanyCustomers(): CustomerLocation[] {
        const allCompanyCustomers = locationData?.map((company: CompanyLocation) => company.customers) || [];
        const flatAllCompanyCustomers = ([] as CustomerLocation[]).concat(...allCompanyCustomers);
        return flatAllCompanyCustomers;
    }
    if (!filteredLocations && locationData) {
        setFilteredLocations(locationData);
    }

    useEffect(() => {
        if (searchTerm === "") {
            setFilteredLocations(locationData);
        }
    }, [searchTerm, locationData]);

    const popover = useRef<HTMLDivElement>(null);

    const popoverTrigger = (
        <Styles.LocationsListDropdown
            isError={(isError || poError) && selectedItems.length === 0}
            data-test-selector="expandLocationPicker"
        >
            {translate("Locations List")}
            {selectedItems.length > 0 && <Styles.Badge variant="legend">{selectedItems.length}</Styles.Badge>}
            <Styles.LocationsListDropdownIcon src={ChevronDown} />
        </Styles.LocationsListDropdown>
    );

    return (
        <GridContainer>
            <Styles.GridItemLocationPicker width={12}>
                <Styles.LabelLocationPicker variant="legend">
                    {translate("Locations")}
                    <br />
                    <Styles.StyledSpan>{translate("* Required")}</Styles.StyledSpan>
                </Styles.LabelLocationPicker>
                <Styles.PopoverLocationPicker
                    contentBodyProps={{
                        as: "div",
                    }}
                    positionFunction={() => {
                        return {
                            position: "absolute",
                            left: 0,
                            width: "100%",
                        };
                    }}
                    insideRefs={[popover]}
                    popoverTrigger={popoverTrigger}
                >
                    <Styles.LocationPickerWrapper ref={popover}>
                        <Styles.LocationPickerHeader>
                            <Typography
                                variant="h4"
                                css={css`
                                    margin-bottom: 0;
                                    font-size: ${pxToRem(16)} !important;
                                `}
                            >
                                {translate("Locations")}
                            </Typography>

                            <Styles.SelectAllButton onClick={() => toggleAllCompanyCustomers(getAllCompanyCustomers())}>
                                {getTotalCustomers() === selectedItems.length
                                    ? translate("Deselect All")
                                    : translate("Select All")}
                            </Styles.SelectAllButton>
                        </Styles.LocationPickerHeader>
                        <Styles.SearchLocationPicker>
                            <TextField
                                value={searchTerm}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    setSearchTerm(event.currentTarget.value)
                                }
                                iconProps={{ src: Search }}
                                placeholder={translate("Search Location")}
                                css={css`
                                    height: 40px !important;
                                    font-size: 0.9rem !important;
                                `}
                            ></TextField>
                        </Styles.SearchLocationPicker>
                        {filteredLocations?.map(company =>
                            renderCompany({ company, toggleAllCompanyCustomers, selectedItems, toggleItem }),
                        )}
                    </Styles.LocationPickerWrapper>
                </Styles.PopoverLocationPicker>
            </Styles.GridItemLocationPicker>
        </GridContainer>
    );
};

const widgetModel: WidgetModule = {
    component: LocationPicker,
    definition: {
        fieldDefinitions: [],
        group: "Common",
    },
};

const MemoizedLocationPicker = React.memo(LocationPicker);
export default widgetModel;
export { MemoizedLocationPicker as LocationPicker };
